
	/*!
  * tiptap vundefined
  * (c) 2018 Scrumpy UG (limited liability)
  * @license MIT
  */

import { markIsActive, nodeIsActive, getMarkAttrs } from 'tiptap-utils';
import { lift, selectParentNode, baseKeymap } from 'prosemirror-commands';
import { undoInputRule, inputRules } from 'prosemirror-inputrules';
import Vue from 'vue';
import { Plugin, EditorState } from 'prosemirror-state';
import { keymap } from 'prosemirror-keymap';
import { setBlockType } from 'tiptap-commands';
import { EditorView } from 'prosemirror-view';
import { Schema, DOMParser, DOMSerializer } from 'prosemirror-model';
import { gapCursor } from 'prosemirror-gapcursor';

function buildMenuActions (ref) {
	var schema = ref.schema;
	var state = ref.state;
	var commands = ref.commands;


	var nodes = Object.entries(schema.nodes)
		.map(function (ref) {
			var name = ref[0];

			var active = function (attrs) {
				if ( attrs === void 0 ) attrs = {};

				return nodeIsActive(state, schema.nodes[name], attrs);
			};
			var command = commands[name] ? commands[name] : function () {};
			return { name: name, active: active, command: command }
		})
		.reduce(function (actions, ref) {
			var obj;

			var name = ref.name;
			var active = ref.active;
			var command = ref.command;
			return (Object.assign({}, actions,
			( obj = {}, obj[name] = {
				active: active,
				command: command,
			}, obj )));
	}, {});

	var marks = Object.entries(schema.marks)
		.map(function (ref) {
			var name = ref[0];

			var active = function () { return markIsActive(state, schema.marks[name]); };
			var attrs = getMarkAttrs(state, schema.marks[name]);
			var command = commands[name] ? commands[name] : function () {};
			return {
				name: name,
				active: active,
				attrs: attrs,
				command: command,
			}
		})
		.reduce(function (actions, ref) {
			var obj;

			var name = ref.name;
			var active = ref.active;
			var attrs = ref.attrs;
			var command = ref.command;
			return (Object.assign({}, actions,
			( obj = {}, obj[name] = {
				active: active,
				attrs: attrs,
				command: command,
			}, obj )));
	}, {});

	return {
		nodes: nodes,
		marks: marks,
	}

}

var keymap$1 = {
	'Mod-BracketLeft': lift,
	Backspace: undoInputRule,
	Escape: selectParentNode,
};

var ComponentView = function ComponentView(component, ref) {
	var node = ref.node;
	var view = ref.view;
	var getPos = ref.getPos;
	var decorations = ref.decorations;
	var editable = ref.editable;

	this.component = component;
	this.node = node;
	this.view = view;
	this.getPos = getPos;
	this.decorations = decorations;
	this.editable = editable;

	this.dom = this.createDOM();
	this.contentDOM = this.vm.$refs.content;
};

ComponentView.prototype.createDOM = function createDOM () {
		var this$1 = this;

	var Component = Vue.extend(this.component);
	this.vm = new Component({
		propsData: {
			node: this.node,
			view: this.view,
			getPos: this.getPos,
			decorations: this.decorations,
			editable: this.editable,
			updateAttrs: function (attrs) { return this$1.updateAttrs(attrs); },
			updateContent: function (content) { return this$1.updateContent(content); },
		},
	}).$mount();
	return this.vm.$el
};

ComponentView.prototype.updateAttrs = function updateAttrs (attrs) {
	if (!this.editable) {
		return
	}

	var transaction = this.view.state.tr.setNodeMarkup(this.getPos(), null, Object.assign({}, this.node.attrs,
		attrs));
	this.view.dispatch(transaction);
};

ComponentView.prototype.updateContent = function updateContent (content) {
	if (!this.editable) {
		return
	}

	var transaction = this.view.state.tr.setNodeMarkup(this.getPos(), this.node.type, { content: content });
	this.view.dispatch(transaction);
};

ComponentView.prototype.ignoreMutation = function ignoreMutation () {
	return true
};

ComponentView.prototype.stopEvent = function stopEvent () {
	return true
};

ComponentView.prototype.update = function update (node, decorations) {
	if (node.type !== this.node.type) {
		return false
	}

	if (node === this.node && this.decorations === decorations) {
		return true
	}

	this.node = node;
	this.decorations = decorations;
	this.vm._props.node = node;
	this.vm._props.decorations = decorations;
	return true
};

ComponentView.prototype.destroy = function destroy () {
	this.vm.$destroy();
};

function initNodeViews(ref) {
  var nodes = ref.nodes;
  var editable = ref.editable;

  var nodeViews = {};

  Object.keys(nodes).forEach(function (nodeName) {
    nodeViews[nodeName] = function (node, view, getPos, decorations) {
      var component = nodes[nodeName];

      return new ComponentView(component, {
        node: node,
        view: view,
        getPos: getPos,
        decorations: decorations,
        editable: editable,
      })
    };
  });

  return nodeViews
}

var Toolbar = function Toolbar(ref) {
	var node = ref.node;
	var editorView = ref.editorView;

	this.editorView = editorView;
	this.node = node;
	this.element = this.node.elm;
	this.element.style.visibility = 'hidden';
	this.element.style.opacity = 0;

	this.editorView.dom.addEventListener('blur', this.hide.bind(this));
};

Toolbar.prototype.update = function update (view, lastState) {
	var state = view.state;

	// Don't do anything if the document/selection didn't change
	if (lastState && lastState.doc.eq(state.doc) && lastState.selection.eq(state.selection)) {
		return
	}

	// Hide the tooltip if the selection is empty
	if (state.selection.empty) {
		this.hide();
		return
	}

	// Otherwise, reposition it and update its content
	this.show();
	var ref = state.selection;
		var from = ref.from;
		var to = ref.to;

	// These are in screen coordinates
	var start = view.coordsAtPos(from);
	var end = view.coordsAtPos(to);

	// The box in which the tooltip is positioned, to use as base
	var box = this.element.offsetParent.getBoundingClientRect();

	// Find a center-ish x position from the selection endpoints (when
	// crossing lines, end may be more to the left)
	var left = Math.max((start.left + end.left) / 2, start.left + 3);
	this.element.style.left = (left - box.left) + "px";
	this.element.style.bottom = (box.bottom - start.top) + "px";
};

Toolbar.prototype.show = function show () {
	this.element.style.visibility = 'visible';
	this.element.style.opacity = 1;
};

Toolbar.prototype.hide = function hide (event) {
	if (event && event.relatedTarget) {
		return
	}

	this.element.style.visibility = 'hidden';
	this.element.style.opacity = 0;
};

Toolbar.prototype.destroy = function destroy () {
	this.editorView.dom.removeEventListener('blur', this.hide);
};

function menuBubble (node) {
	return new Plugin({
		view: function view(editorView) {
			return new Toolbar({ editorView: editorView, node: node })
		},
	})
}

var ExtensionManager = function ExtensionManager(extensions) {
	if ( extensions === void 0 ) extensions = [];

	this.extensions = extensions;
};

var prototypeAccessors = { nodes: { configurable: true },marks: { configurable: true },plugins: { configurable: true },views: { configurable: true } };

prototypeAccessors.nodes.get = function () {
	return this.extensions
		.filter(function (extension) { return extension.type === 'node'; })
		.reduce(function (nodes, ref) {
				var obj;

				var name = ref.name;
				var schema = ref.schema;
				return (Object.assign({}, nodes,
			( obj = {}, obj[name] = schema, obj )));
		}, {})
};

prototypeAccessors.marks.get = function () {
	return this.extensions
		.filter(function (extension) { return extension.type === 'mark'; })
		.reduce(function (marks, ref) {
				var obj;

				var name = ref.name;
				var schema = ref.schema;
				return (Object.assign({}, marks,
			( obj = {}, obj[name] = schema, obj )));
		}, {})
};

prototypeAccessors.plugins.get = function () {
	return this.extensions
		.filter(function (extension) { return extension.plugins; })
		.reduce(function (allPlugins, ref) {
				var plugins = ref.plugins;

				return (allPlugins.concat( plugins ));
		}, [])
};

prototypeAccessors.views.get = function () {
	return this.extensions
		.filter(function (extension) { return ['node', 'mark'].includes(extension.type); })
		.filter(function (extension) { return extension.view; })
		.reduce(function (views, ref) {
				var obj;

				var name = ref.name;
				var view = ref.view;
				return (Object.assign({}, views,
			( obj = {}, obj[name] = view, obj )));
		}, {})
};

ExtensionManager.prototype.keymaps = function keymaps (ref) {
		var schema = ref.schema;

	var extensionKeymaps = this.extensions
		.filter(function (extension) { return ['extension'].includes(extension.type); })
		.filter(function (extension) { return extension.keys; })
		.map(function (extension) { return extension.keys({ schema: schema }); });

	var nodeMarkKeymaps = this.extensions
		.filter(function (extension) { return ['node', 'mark'].includes(extension.type); })
		.filter(function (extension) { return extension.keys; })
		.map(function (extension) { return extension.keys({
			type: schema[((extension.type) + "s")][extension.name],
			schema: schema,
		}); });

	return extensionKeymaps.concat( nodeMarkKeymaps ).map(function (keys) { return keymap(keys); })
};

ExtensionManager.prototype.inputRules = function inputRules$$1 (ref) {
		var schema = ref.schema;

	var extensionInputRules = this.extensions
		.filter(function (extension) { return ['extension'].includes(extension.type); })
		.filter(function (extension) { return extension.inputRules; })
		.map(function (extension) { return extension.inputRules({ schema: schema }); });

	var nodeMarkInputRules = this.extensions
		.filter(function (extension) { return ['node', 'mark'].includes(extension.type); })
		.filter(function (extension) { return extension.inputRules; })
		.map(function (extension) { return extension.inputRules({
			type: schema[((extension.type) + "s")][extension.name],
			schema: schema,
		}); });

	return extensionInputRules.concat( nodeMarkInputRules ).reduce(function (allInputRules, inputRules$$1) { return (allInputRules.concat( inputRules$$1 )); }, [])
};

ExtensionManager.prototype.commands = function commands (ref) {
		var schema = ref.schema;
		var view = ref.view;

	return this.extensions
		.filter(function (extension) { return ['node', 'mark'].includes(extension.type); })
		.filter(function (extension) { return extension.command; })
		.reduce(function (commands, ref) {
				var obj;

				var name = ref.name;
				var type = ref.type;
				var command = ref.command;
				return (Object.assign({}, commands,
			( obj = {}, obj[name] = function (attrs) {
				view.focus();

				var provider = command({
					type: schema[(type + "s")][name],
					attrs: attrs,
					schema: schema,
				});
				var callbacks = Array.isArray(provider) ? provider : [provider];

				callbacks.forEach(function (callback) { return callback(view.state, view.dispatch, view); });
			}, obj )));
		}, {})
};

Object.defineProperties( ExtensionManager.prototype, prototypeAccessors );

var Extension = function Extension(options) {
	if ( options === void 0 ) options = {};

	this.options = Object.assign({}, this.defaultOptions,
		options);
};

var prototypeAccessors$1 = { name: { configurable: true },type: { configurable: true },defaultOptions: { configurable: true },plugins: { configurable: true } };

prototypeAccessors$1.name.get = function () {
	return null
};

prototypeAccessors$1.type.get = function () {
	return 'extension'
};

prototypeAccessors$1.defaultOptions.get = function () {
	return {}
};

prototypeAccessors$1.plugins.get = function () {
	return []
};

Extension.prototype.inputRules = function inputRules$$1 () {
	return []
};

Extension.prototype.keys = function keys () {
	return {}
};

Object.defineProperties( Extension.prototype, prototypeAccessors$1 );

var Node = (function (Extension$$1) {
	function Node(options) {
		if ( options === void 0 ) options = {};

		Extension$$1.call(this, options);
	}

	if ( Extension$$1 ) Node.__proto__ = Extension$$1;
	Node.prototype = Object.create( Extension$$1 && Extension$$1.prototype );
	Node.prototype.constructor = Node;

	var prototypeAccessors = { type: { configurable: true },view: { configurable: true },schema: { configurable: true } };

	prototypeAccessors.type.get = function () {
		return 'node'
	};

	prototypeAccessors.view.get = function () {
		return null
	};

	prototypeAccessors.schema.get = function () {
		return null
	};

	Node.prototype.command = function command () {
		return function () {}
	};

	Object.defineProperties( Node.prototype, prototypeAccessors );

	return Node;
}(Extension));

var DocNode = (function (Node$$1) {
	function DocNode () {
		Node$$1.apply(this, arguments);
	}

	if ( Node$$1 ) DocNode.__proto__ = Node$$1;
	DocNode.prototype = Object.create( Node$$1 && Node$$1.prototype );
	DocNode.prototype.constructor = DocNode;

	var prototypeAccessors = { name: { configurable: true },schema: { configurable: true } };

	prototypeAccessors.name.get = function () {
		return 'doc'
	};

	prototypeAccessors.schema.get = function () {
		return {
			content: 'block+',
		}
	};

	Object.defineProperties( DocNode.prototype, prototypeAccessors );

	return DocNode;
}(Node));

var ParagraphNode = (function (Node$$1) {
	function ParagraphNode () {
		Node$$1.apply(this, arguments);
	}

	if ( Node$$1 ) ParagraphNode.__proto__ = Node$$1;
	ParagraphNode.prototype = Object.create( Node$$1 && Node$$1.prototype );
	ParagraphNode.prototype.constructor = ParagraphNode;

	var prototypeAccessors = { name: { configurable: true },schema: { configurable: true } };

	prototypeAccessors.name.get = function () {
		return 'paragraph'
	};

	prototypeAccessors.schema.get = function () {
		return {
			content: 'inline*',
			group: 'block',
			draggable: false,
			parseDOM: [{
				tag: 'p',
			}],
			toDOM: function () { return ['p', 0]; },
		}
	};

	ParagraphNode.prototype.command = function command (ref) {
		var type = ref.type;

		return setBlockType(type)
	};

	Object.defineProperties( ParagraphNode.prototype, prototypeAccessors );

	return ParagraphNode;
}(Node));

var TextNode = (function (Node$$1) {
	function TextNode () {
		Node$$1.apply(this, arguments);
	}

	if ( Node$$1 ) TextNode.__proto__ = Node$$1;
	TextNode.prototype = Object.create( Node$$1 && Node$$1.prototype );
	TextNode.prototype.constructor = TextNode;

	var prototypeAccessors = { name: { configurable: true },schema: { configurable: true } };

	prototypeAccessors.name.get = function () {
		return 'text'
	};

	prototypeAccessors.schema.get = function () {
		return {
			group: 'inline',
		}
	};

	Object.defineProperties( TextNode.prototype, prototypeAccessors );

	return TextNode;
}(Node));

var builtInNodes = [
	new DocNode(),
	new TextNode(),
	new ParagraphNode() ];

var editor = {

	props: {
		doc: {
			type: Object,
			required: false,
			default: null,
		},
		extensions: {
			type: Array,
			required: false,
			default: function () { return []; },
		},
		editable: {
			type: Boolean,
			default: true,
		},
	},

	data: function data() {
		var allExtensions = new ExtensionManager(builtInNodes.concat( this.extensions ));
		var nodes = allExtensions.nodes;
		var marks = allExtensions.marks;
		var views = allExtensions.views;

		return {
			state: null,
			view: null,
			plugins: [],
			allExtensions: allExtensions,
			schema: null,
			nodes: nodes,
			marks: marks,
			views: views,
			keymaps: [],
			commands: {},
			menuActions: null,
		}
	},

	watch: {

		doc: {
			deep: true,
			handler: function handler() {
				this.setContent(this.doc, true);
			},
		},

	},

	render: function render(createElement) {
		var this$1 = this;

		var slots = [];

		Object
			.entries(this.$scopedSlots)
			.forEach(function (ref) {
				var name = ref[0];
				var slot = ref[1];

				if (name === 'content') {
					this$1.contentNode = slot({});
					slots.push(this$1.contentNode);
				} else if (name === 'menubar') {
					this$1.menubarNode = slot({
						nodes: this$1.menuActions ? this$1.menuActions.nodes : null,
						marks: this$1.menuActions ? this$1.menuActions.marks : null,
						focused: this$1.view ? this$1.view.focused : false,
						focus: this$1.focus,
					});
					slots.push(this$1.menubarNode);
				} else if (name === 'menububble') {
					this$1.menububbleNode = slot({
						nodes: this$1.menuActions ? this$1.menuActions.nodes : null,
						marks: this$1.menuActions ? this$1.menuActions.marks : null,
						focused: this$1.view ? this$1.view.focused : false,
						focus: this$1.focus,
					});
					slots.push(this$1.menububbleNode);
				}
			});

		return createElement('div', {
			class: 'vue-editor',
		}, slots)
	},

	methods: {
		initEditor: function initEditor() {
			this.schema = this.createSchema();
			this.plugins = this.createPlugins();
			this.keymaps = this.createKeymaps();
			this.inputRules = this.createInputRules();
			this.state = this.createState();
			this.clearSlot();
			this.view = this.createView();
			this.commands = this.createCommands();
			this.updateMenuActions();
			this.$emit('init', {
				view: this.view,
				state: this.state,
			});
		},

		createSchema: function createSchema() {
			return new Schema({
				nodes: this.nodes,
				marks: this.marks,
			})
		},

		createPlugins: function createPlugins() {
			return this.allExtensions.plugins
		},

		createKeymaps: function createKeymaps() {
			return this.allExtensions.keymaps({
				schema: this.schema,
			})
		},

		createInputRules: function createInputRules() {
			return this.allExtensions.inputRules({
				schema: this.schema,
			})
		},

		createCommands: function createCommands() {
			return this.allExtensions.commands({
				schema: this.schema,
				view: this.view,
			})
		},

		createState: function createState() {
			return EditorState.create({
				schema: this.schema,
				doc: this.getDocument(),
				plugins: this.plugins.concat( this.getPlugins() ),
			})
		},

		getDocument: function getDocument() {
			if (this.doc) {
				return this.schema.nodeFromJSON(this.doc)
			}

			return DOMParser.fromSchema(this.schema).parse(this.contentNode.elm)
		},

		clearSlot: function clearSlot() {
			this.contentNode.elm.innerHTML = '';
		},

		getPlugins: function getPlugins() {
			var this$1 = this;

			var plugins = [
				inputRules({
					rules: this.inputRules,
				}) ].concat( this.keymaps,
				[keymap(keymap$1)],
				[keymap(baseKeymap)],
				[gapCursor()],
				[new Plugin({
					props: {
						editable: function () { return this$1.editable; },
					},
				})] );

			if (this.menububbleNode) {
				plugins.push(menuBubble(this.menububbleNode));
			}

			return plugins
		},

		createView: function createView() {
			this.contentNode.elm.style.whiteSpace = 'pre-wrap';

			return new EditorView(this.contentNode.elm, {
				state: this.state,
				dispatchTransaction: this.dispatchTransaction,
				nodeViews: initNodeViews({
					nodes: this.views,
					editable: this.editable,
				}),
			})
		},

		destroyEditor: function destroyEditor() {
			if (this.view) {
				this.view.destroy();
			}
		},

		updateMenuActions: function updateMenuActions() {
			this.menuActions = buildMenuActions({
				schema: this.schema,
				state: this.view.state,
				commands: this.commands,
			});
		},

		dispatchTransaction: function dispatchTransaction(transaction) {
			this.state = this.state.apply(transaction);
			this.view.updateState(this.state);
			this.updateMenuActions();

			if (!transaction.docChanged) {
				return
			}

			this.emitUpdate();
		},

		getHTML: function getHTML() {
			var div = document.createElement('div');
			var fragment = DOMSerializer
				.fromSchema(this.schema)
				.serializeFragment(this.state.doc.content);

			div.appendChild(fragment);

			return div.innerHTML
		},

		getJSON: function getJSON() {
			return this.state.doc.toJSON()
		},

		emitUpdate: function emitUpdate() {
			this.$emit('update', {
				getHTML: this.getHTML,
				getJSON: this.getJSON,
				state: this.state,
			});
		},

		getDocFromContent: function getDocFromContent(content) {
			if (typeof content === 'object') {
				return this.schema.nodeFromJSON(content)
			}

			if (typeof content === 'string') {
				var element = document.createElement('div');
				element.innerHTML = content.trim();

				return DOMParser.fromSchema(this.schema).parse(element)
			}

			return false
		},

		setContent: function setContent(content, emitUpdate) {
			if ( content === void 0 ) content = {};
			if ( emitUpdate === void 0 ) emitUpdate = false;

			this.state = EditorState.create({
				schema: this.state.schema,
				doc: this.getDocFromContent(content),
				plugins: this.state.plugins,
			});

			this.view.updateState(this.state);

			if (emitUpdate) {
				this.emitUpdate();
			}
		},

		clearContent: function clearContent(emitUpdate) {
			if ( emitUpdate === void 0 ) emitUpdate = false;

			this.setContent({
				type: 'doc',
				content: [{
					type: 'paragraph',
				}],
			}, emitUpdate);
		},

		focus: function focus() {
			this.view.focus();
		},

	},

	mounted: function mounted() {
		this.initEditor();
	},

	beforeDestroy: function beforeDestroy() {
		this.destroyEditor();
	},

};

var Mark = (function (Extension$$1) {
	function Mark(options) {
		if ( options === void 0 ) options = {};

		Extension$$1.call(this, options);
	}

	if ( Extension$$1 ) Mark.__proto__ = Extension$$1;
	Mark.prototype = Object.create( Extension$$1 && Extension$$1.prototype );
	Mark.prototype.constructor = Mark;

	var prototypeAccessors = { type: { configurable: true },view: { configurable: true },schema: { configurable: true } };

	prototypeAccessors.type.get = function () {
		return 'mark'
	};

	prototypeAccessors.view.get = function () {
		return null
	};

	prototypeAccessors.schema.get = function () {
		return null
	};

	Mark.prototype.command = function command () {
		return function () {}
	};

	Object.defineProperties( Mark.prototype, prototypeAccessors );

	return Mark;
}(Extension));

export { editor as Editor, Extension, Node, Mark, Plugin };



//////////////////
// WEBPACK FOOTER
// ./node_modules/tiptap/dist/tiptap.esm.js
// module id = null
// module chunks = 